/**
 * Golden suites for customer-owned providers — ADR-0073 §6 / D-105-18 (V105-2-6).
 *
 * One suite per workspace-scope LLM task that a customer provider can serve,
 * run against the task's real templates. Every case is synthetic and
 * GAC Pro-authored (reference class): evaluating a customer's provider sends
 * none of their data. A task is enabled for a provider only when its latest
 * eval for the current (model, prompt version, suite hash) passed; a template
 * or case change changes the suite hash, so every provider re-evaluates.
 *
 * Not suited (never enabled on a customer provider):
 *   - questionnaire.match_embed: the register index is bound to its own
 *     embedding model (ADR-0020); federal matching is lexical.
 *   - infra.verdict_suggest: templates arrive with Tracks 3/4.
 *
 * Agent thresholds reuse AGENT_EVAL_REGISTRY; other tasks require 0.8.
 */
import { WORKSPACE_TASK_KEYS } from '../egress/tasks.js';
import { AGENT_EVAL_REGISTRY } from './agent-eval-infrastructure.js';
import { EXTRACT_TEMPLATE, EXTRACT_SCHEMA } from './questionnaire-extractor-service.js';
import { MAPPING_TEMPLATE, MAPPING_SCHEMA } from './questionnaire-xlsx-mapper-service.js';
import { RERANK_TEMPLATE, RERANK_SCHEMA } from './questionnaire-matcher-service.js';
import { DRAFT_TEMPLATE, DRAFT_SCHEMA } from './questionnaire-drafter-service.js';
import { EXTRACTION_TEMPLATE, EXTRACTION_INPUT_TEMPLATE, EXTRACTION_SCHEMA } from './control-spec-extraction-service.js';
import { COACH_TEMPLATE, COACH_SCHEMA } from './remediation-coach-service.js';
import { POLICY_DRAFT_TEMPLATE, POLICY_DRAFT_SCHEMA } from './policy-drafter-service.js';
import { INCIDENT_TEMPLATE, INCIDENT_INPUT_TEMPLATE } from './incident-notification-drafter-service.js';
import { A1_TEMPLATE, A1_OUTPUT_SCHEMA } from './agent-intake-elicitation-service.js';
import { A3_TEMPLATE, A3B_TEMPLATE, A3_OUTPUT_SCHEMA, A3B_OUTPUT_SCHEMA } from './agent-disclosure-drafting-service.js';
import { A4_TEMPLATE, A4_A5_OUTPUT_SCHEMA } from './agent-remediation-drift-service.js';
import { A6A_TEMPLATE, A6A_OUTPUT_SCHEMA } from './agent-gap-questioner-service.js';
import { A6B_TEMPLATE, A6B_OUTPUT_SCHEMA } from './agent-options-advisor-service.js';
import { A6C_TEMPLATE, A6C_OUTPUT_SCHEMA } from './agent-board-narrator-service.js';
import { A8_TEMPLATE, A8_OUTPUT_SCHEMA } from './agent-evidence-auditor-service.js';
const DEFAULT_THRESHOLD = 0.8;
const thresholdFor = (agentId) => AGENT_EVAL_REGISTRY.find((d) => d.agent_id === agentId)?.threshold ?? DEFAULT_THRESHOLD;
const schema = (s) => s;
// ---- tiny, total checkers --------------------------------------------------------
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (typeof v === 'string' ? v : '');
const has = (s, ...needles) => needles.filter((n) => s.toLowerCase().includes(n.toLowerCase())).length / needles.length;
const mean = (...xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
const bool = (b) => (b ? 1 : 0);
// Fictional, GAC Pro-authored fixtures (reference class).
const SYSTEM = 'Example Resume Ranker';
const SYSTEM_ID = 'sys-example-1';
export const PROVIDER_GOLDEN_SUITES = {
    'questionnaire.extract': {
        task: 'questionnaire.extract', template: EXTRACT_TEMPLATE, schemaHint: schema(EXTRACT_SCHEMA), threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'qx-1',
                input: 'Section IS.01 Information Security\n1. Do you encrypt customer data at rest?\n2. Is multi-factor authentication enforced for administrators?\nPlease answer every question.',
                check: (o) => { const qs = arr(obj(o).questions).map((q) => str(obj(q).text)).join(' '); return mean(bool(arr(obj(o).questions).length >= 2), has(qs, 'encrypt', 'multi-factor')); },
            }],
    },
    'questionnaire.map_columns': {
        task: 'questionnaire.map_columns', template: MAPPING_TEMPLATE, schemaHint: schema(MAPPING_SCHEMA), threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'qm-1',
                input: JSON.stringify({ headers: ['ID', 'Control Area', 'Question', 'Response', 'Evidence Link'], rows: [{ ID: '1', 'Control Area': 'Access', Question: 'Is MFA enforced?', Response: 'Yes', 'Evidence Link': 'https://example.com/mfa' }] }),
                check: (o) => mean(bool(str(obj(o).question_col) === 'Question'), bool(str(obj(o).response_col) === 'Response')),
            }],
    },
    'questionnaire.match_rerank': {
        task: 'questionnaire.match_rerank', template: RERANK_TEMPLATE, schemaHint: schema(RERANK_SCHEMA), threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'qr-1',
                input: JSON.stringify([{ question_id: 'q1', question: 'Do you encrypt data at rest?', section: null, candidates: [
                            { risk_item_id: 'RR-ENC', title: 'Encryption at rest', frameworks: ['soc2'], similarity: 0.7 },
                            { risk_item_id: 'RR-HR', title: 'Background checks for staff', frameworks: ['soc2'], similarity: 0.4 },
                        ] }]),
                check: (o) => { const r = obj(arr(obj(o).results)[0]); const cites = arr(r.citations).map((c) => str(obj(c).risk_item_id)); return bool(cites[0] === 'RR-ENC' && !cites.includes('RR-INVENTED')); },
            }],
    },
    'questionnaire.draft': {
        task: 'questionnaire.draft', template: DRAFT_TEMPLATE, schemaHint: schema(DRAFT_SCHEMA), threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'qd-1',
                input: JSON.stringify([{ question_id: 'q1', section: null, question: 'Do you encrypt data at rest?', citations: [{ risk_item_id: 'RR-ENC', title: 'Encryption at rest', excerpt: 'Data at rest is encrypted with managed keys.', frameworks: ['soc2'], relevance: 0.9, evidence: null }] }]),
                check: (o) => { const r = obj(arr(obj(o).results)[0]); return mean(bool(str(r.question_id) === 'q1'), has(str(r.draft_text), 'RR-ENC')); },
            }],
    },
    'control_spec.extract': {
        task: 'control_spec.extract', template: EXTRACTION_TEMPLATE, inputTemplate: EXTRACTION_INPUT_TEMPLATE, schemaHint: schema(EXTRACTION_SCHEMA), threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'cs-1',
                slots: { title: 'Access reviews', description: 'Review user access periodically.', framework_refs: 'soc2', document_text: 'Managers review all user access every 90 days and record the review in the access log.' },
                check: (o) => { const x = obj(o); return mean(bool(arr(x.accepted_signals).length > 0), bool(typeof x.confidence === 'number' && x.confidence > 0)); },
            }],
    },
    'remediation.coach': {
        task: 'remediation.coach', template: COACH_TEMPLATE, schemaHint: schema(COACH_SCHEMA), options: { maxTokens: 900, temperature: 0.1 }, threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'rc-1',
                slots: { control_id: 'SEC-IAM-01', check: 'MFA enforced (idp_mfa_enforced)', evidence: 'hash-example (observed 2026-01-01)', snapshot: '{"single_factor_rules":2}', guidance: '1. Require MFA in every sign-on policy.' },
                check: (o) => { const x = obj(o); return mean(bool(arr(x.steps).length > 0), bool(str(x.assessor_reply).length > 0), bool(typeof x.confidence === 'number')); },
            }],
    },
    'policy.draft': {
        task: 'policy.draft', template: POLICY_DRAFT_TEMPLATE, schemaHint: schema(POLICY_DRAFT_SCHEMA), threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'pd-1',
                input: JSON.stringify({ sections: [{ section_id: 'purpose', section_kind: 'purpose', section_title: 'Purpose', target_level: 2, scope_summary: 'Access control', category_policy_context: null, grounded: true, organization: { organization_name: 'Example Corp' }, frameworks_in_scope: [], team: null, readiness: null, cited_artifacts: [], included_items: [{ risk_item_id: 'RR-IAM', title: 'Access control', description: 'Restrict access to authorized users.' }] }] }),
                check: (o) => { const r = obj(arr(obj(o).results)[0]); return mean(bool(str(r.section_id) === 'purpose'), bool(str(r.body).length > 40)); },
            }],
    },
    'incident.notification_draft': {
        task: 'incident.notification_draft', template: INCIDENT_TEMPLATE, inputTemplate: INCIDENT_INPUT_TEMPLATE,
        schemaHint: { type: 'object', properties: { subject: { type: 'string' }, body: { type: 'string' } } }, threshold: DEFAULT_THRESHOLD,
        cases: [{
                id: 'in-1',
                slots: { regulator: 'Example Data Protection Authority', statute: 'Example Act s.12', deadline: '2026-01-04', title: 'Misdirected report email', severity: 'moderate', jurisdiction: 'EX', data_classes: 'contact details', affected: '3', reported_at: '2026-01-01', organization: 'Example Corp' },
                check: (o) => { const x = obj(o); return mean(bool(str(x.subject).length > 0), has(str(x.body), 'Example Data Protection Authority')); },
            }],
    },
    'agent.a1.intake': {
        task: 'agent.a1.intake', template: A1_TEMPLATE, schemaHint: schema(A1_OUTPUT_SCHEMA), options: { temperature: 0.1, maxTokens: 4096 }, threshold: thresholdFor('A1'),
        cases: [{
                id: 'a1-1',
                slots: { boolean_fields: 'consequential_decision, employment_decision', scalar_fields: 'decision_role', array_fields: 'deployment_jurisdictions', system_name: SYSTEM, description: 'Ranks job applicants by resume for recruiters in Colorado; a recruiter makes the final decision.', documents_section: '', known_jurisdictions_section: '' },
                check: (o) => { const x = obj(o); return mean(bool(str(x.purpose).length > 0), bool(arr(x.field_proposals).length > 0)); },
            }],
    },
    'agent.a3.disclosure_draft': {
        task: 'agent.a3.disclosure_draft', template: A3_TEMPLATE, schemaHint: schema(A3_OUTPUT_SCHEMA), options: { temperature: 0.1, maxTokens: 4096 }, threshold: thresholdFor('A3'),
        cases: [{
                id: 'a3-1',
                slots: { system_name: SYSTEM, system_id: SYSTEM_ID, system_card_section: JSON.stringify({ purpose: 'Rank resumes', models: [{ name: 'Example-LM', provider: 'Example AI' }] }), evidence_count: '0', evidence_section: 'No evidence items.', prior_version_section: 'No prior version.' },
                check: (o) => { const x = obj(o); return mean(bool(arr(x.fields).length > 0), bool(str(x.entry_review_state) === 'draft' || !('entry_review_state' in x))); },
            }],
    },
    'agent.a3b.disclosure_qa': {
        task: 'agent.a3b.disclosure_qa', template: A3B_TEMPLATE, schemaHint: schema(A3B_OUTPUT_SCHEMA), options: { temperature: 0.1, maxTokens: 4096 }, threshold: thresholdFor('A3b'),
        cases: [{
                id: 'a3b-1',
                slots: { system_name: SYSTEM, system_id: SYSTEM_ID, release_id: 'rel-1', disclosure_section: JSON.stringify({ models: [{ name: 'Example-LM' }] }), questions_section: '- [q1] What is the favourite colour of the vendor CEO?' },
                // Grounded or nothing: an unanswerable question must not be answered.
                check: (o) => { const x = obj(o); return bool(arr(x.answers).length === 0 && arr(x.unanswerable).length >= 1); },
            }],
    },
    'agent.a4.remediation_drift': {
        task: 'agent.a4.remediation_drift', template: A4_TEMPLATE, schemaHint: schema(A4_A5_OUTPUT_SCHEMA), options: { temperature: 0.1, maxTokens: 4096 }, threshold: thresholdFor('A4'),
        cases: [{
                id: 'a4-1',
                slots: { system_name: SYSTEM, system_id: SYSTEM_ID, denial_count: '1', denials_section: '- [REQ-1] OBL-1: missing bias assessment', drift_count: '0', drift_section: 'No drift events.' },
                check: (o) => { const s = arr(obj(o).remediation_suggestions).map((x) => str(obj(x).requirement_id)); return bool(s.includes('REQ-1')); },
            }],
    },
    'agent.a6a.gap_questions': {
        task: 'agent.a6a.gap_questions', template: A6A_TEMPLATE, schemaHint: schema(A6A_OUTPUT_SCHEMA), threshold: thresholdFor('A6a'),
        cases: [{
                id: 'a6a-1',
                slots: { observed: '- Slot: identity | Claim: SSO configured | Strength: strong | Source: connector', gaps: '- Slot: logging | Gap: missing | Reason: no log source' },
                check: (o) => { const qs = arr(obj(o).questions).map((q) => str(obj(q).slot_id)); return bool(qs.includes('logging') && !qs.includes('identity')); },
            }],
    },
    'agent.a6b.options': {
        task: 'agent.a6b.options', template: A6B_TEMPLATE, schemaHint: schema(A6B_OUTPUT_SCHEMA), threshold: thresholdFor('A6b'),
        cases: [{
                id: 'a6b-1',
                slots: { gaps: '- Slot: logging | Severity: high | Effort: small', options: '  - logging: Example Log Service (good)' },
                check: (o) => bool(arr(obj(o).slot_rationales).length > 0),
            }],
    },
    'agent.a6c.board_narrative': {
        task: 'agent.a6c.board_narrative', template: A6C_TEMPLATE, schemaHint: schema(A6C_OUTPUT_SCHEMA), threshold: thresholdFor('A6c'),
        cases: [{
                id: 'a6c-1',
                slots: { executive_summary: 'Two slots are not yet assessed; identity controls are observed.', sections: '### Identity [observed]\nSSO is configured.', unassessed: 'logging, backup' },
                check: (o) => has(str(obj(o).executive_summary), 'not yet assessed'),
            }],
    },
    'agent.a8.evidence_audit': {
        task: 'agent.a8.evidence_audit', template: A8_TEMPLATE, schemaHint: schema(A8_OUTPUT_SCHEMA), options: { temperature: 0.1, maxTokens: 4096 }, threshold: thresholdFor('A8'),
        cases: [{
                id: 'a8-1',
                slots: { system_name: SYSTEM, system_id: SYSTEM_ID, as_of: '2026-01-01', obligation_count: '1', obligations_section: '- [OBL-1] Bias assessment (example-act) — evidence REQUIRED', evidence_count: '0', evidence_section: 'No evidence bindings.', disclosure_section: 'Disclosure status not provided.' },
                check: (o) => { const f = arr(obj(o).findings).map((x) => str(obj(x).obligation_id)); return bool(f.includes('OBL-1')); },
            }],
    },
};
/** Workspace tasks a customer provider can never serve (see the header). */
export const NOT_SUITED_TASKS = ['questionnaire.match_embed', 'infra.verdict_suggest'];
/** Every workspace task has a suite or is explicitly not suited (checked by test). */
export function unsuitedWorkspaceTasks() {
    return WORKSPACE_TASK_KEYS.filter((t) => !PROVIDER_GOLDEN_SUITES[t] && !NOT_SUITED_TASKS.includes(t));
}
//# sourceMappingURL=provider-golden-suites.js.map